/**
 * WebSocket Connection Handler
 * Handles WebSocket connection lifecycle: Open, Close, and Error events
 * Includes WebSocketRouter for connection management
 */

import type { ConnectionState, ChannelHandler } from './inbound';
import { getMessageRouter } from './inbound';
import { HealthCheck } from './healthCheck';
import { getWebSocketUrl } from './payloadHelpers';
import type { Channel, Actor, Status, BodyKind, MessageType, Origin } from './outbound';
import { Connection, CommunicationManager } from './outbound';

// -----------------------------
// WebSocket Router Configuration
// -----------------------------

/**
 * WebSocket router configuration
 */
export interface WebSocketRouterConfig {
    url: string;
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
    heartbeatInterval?: number;
    connectionMonitorInterval?: number; // Interval in minutes to check connection health
}

// -----------------------------
// Utility functions for WebSocketRouter
// -----------------------------

function _isoNow(): string {
    return new Date().toISOString();
}

function _logger() {
    return {
        info: (msg: string) => console.log(msg),
        error: (msg: string) => console.error(msg),
        warn: (msg: string) => console.warn(msg),
        debug: (msg: string) => console.debug(msg)
    };
}

// -----------------------------
// WebSocket Router (Connection Management)
// -----------------------------

/**
 * WebSocketRouter - manages WebSocket connection and delegates message routing to inbound.ts
 * This class handles:
 * - WebSocket connection (connect, disconnect, reconnect)
 * - Sending messages via CommunicationManager
 * - Delegates message receiving/routing to MessageRouter in inbound.ts
 */
export class WebSocketRouter {
    private config: WebSocketRouterConfig;
    private websocket: WebSocket | null = null;
    private conn: Connection | null = null;
    private comm: CommunicationManager | null = null;
    private connectionState: ConnectionState = 'disconnected';
    private reconnectAttempts = 0;
    private reconnectTimer: number | null = null;
    private connectionMonitorTimer: number | null = null;
    private healthStatusMonitoringTimer: number | null = null;
    private isConnecting: boolean = false; // Flag to prevent multiple simultaneous connection attempts
    private connectionTimeout: number | null = null; // Timeout for connection attempt
    private connectionTimeoutStartTime: number | null = null; // Track when connection attempt started

    // Health check - handles ping/pong and connection status
    private healthCheck: HealthCheck;

    // Message router from inbound.ts - handles receiving and routing messages
    private messageRouter = getMessageRouter();

    // State change listeners
    private stateListeners: Set<(state: ConnectionState) => void> = new Set();

    constructor(config: WebSocketRouterConfig) {
        this.config = {
            reconnectInterval: 5 * 60 * 1000, // 5 minutes
            maxReconnectAttempts: 10,
            heartbeatInterval: 30000,
            connectionMonitorInterval: 5, // Default: check every 5 minutes
            ...config
        };

        // Initialize health check
        this.healthCheck = new HealthCheck({
            onStateChange: (state) => {
                this.setConnectionState(state, true); // Skip health check update to avoid loop
            }
        });

        // Start health status monitoring as soon as application starts
        this.startWSHealthStatusMonitoring();
    }

    /**
     * Get current connection state
     * Delegates to healthCheck.getConnectionState() as the single source of truth
     */
    getState(): ConnectionState {
        return this.healthCheck.getConnectionState();
    }

    /**
     * Get health check instance
     * Use this to access health check methods like getConnectionState(), getHealthStatus(), etc.
     */
    getHealthCheck(): HealthCheck {
        return this.healthCheck;
    }

    /**
     * Get the CommunicationManager for sending messages
     */
    getCommunicationManager(): CommunicationManager | null {
        return this.comm;
    }

    /**
     * Get the Connection instance
     */
    getConnection(): Connection | null {
        return this.conn;
    }

    /**
     * Listen for connection state changes
     */
    onStateChange(callback: (state: ConnectionState) => void): () => void {
        this.stateListeners.add(callback);
        return () => this.stateListeners.delete(callback);
    }

    /**
     * Register a handler for a specific channel
     * Delegates to MessageRouter in inbound.ts
     */
    setChannelHandler(channel: Channel, handler: ChannelHandler): void {
        this.messageRouter.setChannelHandler(channel, handler);
    }

    /**
     * Remove a channel handler
     * Delegates to MessageRouter in inbound.ts
     */
    removeChannelHandler(channel: Channel): void {
        this.messageRouter.removeChannelHandler(channel);
    }

    /**
     * Connect to WebSocket server
     */
    connect(): void {
        // Check current state (from health check, single source of truth)
        const currentState = this.getState();
        
        if (currentState === 'connected') {
            return;
        }

        // Prevent multiple simultaneous connection attempts
        if (this.isConnecting) {
            console.log('🔌 [connect] Connection attempt already in progress, skipping...');
            // Check if the connection is stuck (WebSocket exists but never opened)
            if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING && this.connectionTimeoutStartTime) {
                const timeSinceStart = Date.now() - this.connectionTimeoutStartTime;
                if (timeSinceStart > 10000) { // 10 seconds timeout
                    console.warn('🔌 [connect] Connection stuck in CONNECTING state for >10s, resetting...');
                    this.cleanupStuckConnection();
                    // Retry connection after cleanup
                    setTimeout(() => this.connect(), 1000);
                }
            }
            return;
        }

        this.isConnecting = true;
        this.connectionTimeoutStartTime = Date.now();
        console.log('🔌 [connect] Creating new WebSocket connection to:', this.config.url);

        try {
            this.websocket = new WebSocket(this.config.url);
            console.log('🔌 [connect] WebSocket object created, readyState:', this.websocket.readyState);
            
            // Set up connection timeout (10 seconds)
            this.connectionTimeout = window.setTimeout(() => {
                if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING) {
                    console.error('🔌 [connect] Connection timeout: WebSocket did not open within 10 seconds');
                    this.cleanupStuckConnection();
                    this.setConnectionState('disconnected');
                    this.scheduleReconnect();
                }
            }, 10000); // 10 second timeout
            
            this.setupWebSocketHandlers();
            console.log('🔌 [connect] WebSocket handlers set up, waiting for connection (10s timeout)...');
        } catch (error) {
            console.error('🔌 [connect] ERROR: Failed to create WebSocket:', error);
            _logger().error(`Failed to create WebSocket: ${error}`);
            this.clearConnectionTimeout();
            this.isConnecting = false;
            this.setConnectionState('disconnected');
            this.scheduleReconnect();
        }
    }

    /**
     * Disconnect from WebSocket server
     */
    disconnect(): void {
        this.stopReconnect();
        this.stopConnectionMonitor();
        this.clearConnectionTimeout();

        if (this.websocket) {
            this.websocket.close();
            this.websocket = null;
        }

        if (this.conn) {
            this.conn.stopWriter();
            this.conn = null;
        }

        this.comm = null;
        this.isConnecting = false;
        this.connectionTimeoutStartTime = null;
        this.setConnectionState('disconnected');
    }

    /**
     * Send a message using the emit pattern (matches backend)
     */
    emit(
        channel: Channel,
        actor: Actor,
        status: Status,
        kind: BodyKind,
        payload: Record<string, any>
    ): boolean {
        // Allow health pings even if not fully connected yet (during initial connection)
        const isHealthPing = channel === 'health' && payload.message === 'ping';
        
        // Health check manager handles its own pings, but we allow them through
        if (!this.comm || (!isHealthPing && this.connectionState !== 'connected')) {
            _logger().error('Cannot emit: not connected');
            return false;
        }

        return this.comm.emit(channel, actor, status, kind, payload);
    }

    /**
     * Send a request to a channel (convenience method)
     *
     * Note: Cannot send to 'logs' channel - logs are one-way (backend → frontend)
     */
    sendRequest(
        channel: Channel,
        kind: BodyKind,
        payload: Record<string, any>,
        options?: {
            userId?: string;
            projectId?: string;
            featureId?: string;
            page?: string;
        }
    ): boolean {
        // Logs channel is one-way (backend → frontend only)
        if (channel === 'logs') {
            _logger().error('Cannot send to logs channel - logs are one-way (backend → frontend)');
            return false;
        }

        if (!this.conn || !this.comm || this.connectionState !== 'connected') {
            _logger().error('Cannot send request: not connected');
            return false;
        }

        // Create origin for new request (every payload should have projectId, userId, featureId when available)
        const messageId = crypto.randomUUID();
        const timestamp = _isoNow();
        const threadId = this.conn.channelContext.get(channel)?.origin.threadId ||
                         `thread_${channel}_${Date.now()}`;

        const origin: Origin = {
            threadId,
            originMessageId: messageId,
            originActor: 'user',
            origints: timestamp,
            ...(options?.userId && { userId: options.userId }),
            ...(options?.projectId && { projectId: options.projectId }),
            ...(options?.featureId && { featureId: options.featureId }),
            ...(options?.page && { page: options.page })
        };

        // Set channel context for this request
        this.conn.setChannelContext(channel, origin, messageId, 'user', timestamp);

        // Send via emit
        return this.emit(channel, 'user', 'connected', kind, payload);
    }

    /**
     * Set up channel context (for tracking request/response correlation)
     */
    setChannelContext(
        channel: Channel,
        origin: Origin,
        messageId: string,
        actor: Actor,
        ts: string
    ): void {
        if (this.conn) {
            this.conn.setChannelContext(channel, origin, messageId, actor, ts);
        }
    }

    // -----------------------------
    // Private methods
    // -----------------------------

    private setupWebSocketHandlers(): void {
        if (!this.websocket) {
            console.error('🔌 [setupWebSocketHandlers] ERROR: websocket is null, cannot setup handlers');
            return;
        }

        console.log('🔌 [setupWebSocketHandlers] Setting up WebSocket event handlers...');

        this.websocket.onopen = () => {
            console.log('🔌 [onopen] WebSocket connection opened successfully');
            this.clearConnectionTimeout(); // Clear timeout since connection succeeded
            this.reconnectAttempts = 0;
            this.isConnecting = false; // Connection attempt succeeded
            this.connectionTimeoutStartTime = null;

            // Create Connection and CommunicationManager (OUTBOUND responsibility)
            console.log('🔌 [onopen] Creating Connection and CommunicationManager...');
            this.conn = new Connection(this.websocket!, crypto.randomUUID());
            this.comm = new CommunicationManager(this.conn);
            console.log('🔌 [onopen] Connection and CommunicationManager created:', {
                hasConn: !!this.conn,
                hasComm: !!this.comm,
                websocketReadyState: this.websocket?.readyState
            });

            // Step 1: Initialize health check
            console.log('🔌 [onopen] Step 1: Initializing health check...');
            this.healthCheck.initialize(this.conn, this.comm);
            console.log('🔌 [onopen] Health check initialized:', {
                hasConn: !!this.conn,
                hasComm: !!this.comm,
                currentState: this.healthCheck.getConnectionState()
            });
            
            // Step 2: Set up health channel handler for pong responses FIRST
            // This MUST be done before sending any pings so we can receive the pong response
            const existingHandler = this.messageRouter.getChannelHandler('health');
            this.messageRouter.setChannelHandler('health', (payload) => {
                // Receive pong from backend using healthCheck.receive()
                // This is the INBOUND handler - healthCheck.send() handles OUTBOUND pings
                this.healthCheck.receive(payload);
                // Call existing handler if it exists
                if (existingHandler) {
                    existingHandler(payload);
                }
            });
            
            // Step 3: Start health check system (sets up interval timer)
            console.log('🔌 [onopen] Step 3: Starting health check system (interval timer)...');
            this.healthCheck.start();
            
            // Step 4: Send initial ping NOW that handler is set up
            // Handler is ready, so we can send ping and expect pong response
            console.log('🔌 [onopen] Step 4: Sending initial health ping (handler is ready to receive pong)...');
            this.healthCheck.send();
            console.log('🔌 [onopen] Initial health ping sent, waiting for pong response...');
        };

        this.websocket.onclose = (event) => {
            console.log('🔌 [onclose] WebSocket connection closed:', {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                websocketReadyState: this.websocket?.readyState
            });
            
            this.clearConnectionTimeout(); // Clear timeout
            this.isConnecting = false; // Connection attempt finished (failed)
            this.connectionTimeoutStartTime = null;
            
            // Reset MessageRouter timestamps on disconnect
            this.messageRouter.resetTimestamps();

            if (this.conn) {
                this.conn.stopWriter();
                console.log('🔌 [onclose] Connection writer stopped');
            }

            const healthStatus = this.healthCheck.getHealthStatus();
            console.log('🔌 [onclose] Health status at close:', {
                lastPongTimestamp: healthStatus.lastPongTimestamp,
                status: healthStatus.status,
                timeSinceLastPong: healthStatus.lastPongTimestamp ? Date.now() - healthStatus.lastPongTimestamp : null
            });
            this.setConnectionState('disconnected');
            this.scheduleReconnect();
        };

        this.websocket.onerror = (error) => {
            console.error('🔌 [onerror] WebSocket error occurred:', {
                error,
                websocketReadyState: this.websocket?.readyState,
                url: this.config.url,
                hasConn: !!this.conn,
                hasComm: !!this.comm
            });
            this.clearConnectionTimeout(); // Clear timeout on error
            this.isConnecting = false; // Connection attempt failed
            this.connectionTimeoutStartTime = null;
            const healthStatus = this.healthCheck.getHealthStatus();
            console.error('🔌 [onerror] Health status at error:', {
                lastPongTimestamp: healthStatus.lastPongTimestamp,
                status: healthStatus.status
            });
            this.setConnectionState('disconnected');
        };

        // Delegate message handling to MessageRouter in inbound.ts
        this.websocket.onmessage = (event) => {
            this.messageRouter.handleIncomingMessage(event.data, this.conn);
        };
    }

    private setConnectionState(state: ConnectionState, skipHealthCheckUpdate: boolean = false): void {
        const previousState = this.connectionState;
        this.connectionState = state;
        
        // Update health check manager with current state (unless it's the health check that triggered this)
        if (!skipHealthCheckUpdate) {
            this.healthCheck.setConnectionState(state);
        }
        
        // Note: Connection monitor is disabled - WSHealthStatusMonitoring handles all reconnection
        // WSHealthStatusMonitoring checks every 5 minutes when disconnected and every 2 minutes when connected
        // No need for separate connection monitor that would cause duplicate reconnection attempts
        
        this.stateListeners.forEach(listener => {
            try {
                listener(state);
            } catch (error) {
                _logger().error(`Error in state listener: ${error}`);
            }
        });
    }

    private scheduleReconnect(): void {
        if (this.reconnectAttempts >= (this.config.maxReconnectAttempts || 10)) {
            _logger().error('Max reconnect attempts reached');
            this.setConnectionState('disconnected');
            return;
        }

        this.reconnectAttempts++;

        this.reconnectTimer = window.setTimeout(() => {
            this.connect();
        }, this.config.reconnectInterval);
    }

    private stopReconnect(): void {
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.reconnectAttempts = 0;
    }

    /**
     * Clear connection timeout
     */
    private clearConnectionTimeout(): void {
        if (this.connectionTimeout !== null) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
        }
    }

    /**
     * Clean up stuck connection (when WebSocket is stuck in CONNECTING state)
     */
    private cleanupStuckConnection(): void {
        console.warn('🔌 [cleanupStuckConnection] Cleaning up stuck WebSocket connection...');
        this.clearConnectionTimeout();
        
        if (this.websocket) {
            try {
                // Force close the stuck connection
                if (this.websocket.readyState === WebSocket.CONNECTING || 
                    this.websocket.readyState === WebSocket.OPEN) {
                    this.websocket.close();
                }
            } catch (error) {
                console.error('🔌 [cleanupStuckConnection] Error closing stuck WebSocket:', error);
            }
            this.websocket = null;
        }

        if (this.conn) {
            try {
                this.conn.stopWriter();
            } catch (error) {
                console.error('🔌 [cleanupStuckConnection] Error stopping writer:', error);
            }
            this.conn = null;
        }

        this.comm = null;
        this.isConnecting = false;
        this.connectionTimeoutStartTime = null;
        console.warn('🔌 [cleanupStuckConnection] Stuck connection cleaned up');
    }

    /**
     * Check if connection is disconnected and reconnect if needed
     * This is called periodically when disconnected to attempt reconnection
     * NOTE: WSHealthStatusMonitoring already handles reconnection every 5 minutes,
     * so this monitor is primarily for logging/status updates, not active reconnection
     */
    private checkAndReconnect(): void {
        // Get current state from health check (single source of truth)
        const currentState = this.getState();
        
        // Only attempt reconnect if state is 'disconnected' and not already connecting
        // WSHealthStatusMonitoring handles primary reconnection, this is a backup
        if (currentState === 'disconnected' && !this.isConnecting) {
            console.log(`🔌 [ConnectionMonitor] WebSocket state: ${currentState}, attempting reconnection...`);
            this.connect();
        }
    }

    /**
     * Start the periodic connection monitor
     * Checks connection health every N minutes and reconnects if needed
     */
    private startConnectionMonitor(): void {
        // Only start if not already running
        if (this.connectionMonitorTimer !== null) {
            return;
        }

        const intervalMs = (this.config.connectionMonitorInterval || 2) * 60 * 1000; // Convert minutes to milliseconds

        this.connectionMonitorTimer = window.setInterval(() => {
            this.checkAndReconnect();
        }, intervalMs);
    }

    /**
     * Stop the periodic connection monitor
     */
    private stopConnectionMonitor(): void {
        if (this.connectionMonitorTimer !== null) {
            clearInterval(this.connectionMonitorTimer);
            this.connectionMonitorTimer = null;
        }
    }

    /**
     * Start WS Health Status Monitoring
     * Continuously monitors connection state and triggers health checks
     */
    private startWSHealthStatusMonitoring(): void {
        // Stop any existing monitoring
        this.stopWSHealthStatusMonitoring();
        
        // Start monitoring immediately
        this.WSHealthStatusMonitoring();
    }

    /**
     * Stop WS Health Status Monitoring
     */
    private stopWSHealthStatusMonitoring(): void {
        if (this.healthStatusMonitoringTimer !== null) {
            clearTimeout(this.healthStatusMonitoringTimer);
            this.healthStatusMonitoringTimer = null;
        }
    }

    /**
     * WS Health Status Monitoring
     * Fetches current connectionState from healthCheck
     * - If disconnected: wait 5 minutes, then attempt to reconnect
     * - If connected: sleep for 2 minutes
     * Then loops continuously
     */
    private WSHealthStatusMonitoring(): void {
        // Get current connection state (delegates to healthCheck.getConnectionState())
        const connectionState = this.getState();
        
        if (connectionState === 'disconnected') {
            // Disconnected - wait 5 minutes, then attempt to reconnect
            this.healthStatusMonitoringTimer = window.setTimeout(() => {
                this.connect();
                // Continue monitoring
                this.WSHealthStatusMonitoring();
            }, 5 * 60 * 1000); // 5 minutes
        } else {
            // Connected - sleep for 2 minutes
            this.healthStatusMonitoringTimer = window.setTimeout(() => {
                // Continue monitoring
                this.WSHealthStatusMonitoring();
            }, 2 * 60 * 1000); // 2 minutes
        }
    }
}

// -----------------------------
// Factory functions
// -----------------------------

/**
 * Create a new WebSocket router instance
 */
export function createWebSocketRouter(url: string, config?: Partial<WebSocketRouterConfig>): WebSocketRouter {
    return new WebSocketRouter({ url, ...config });
}

// -----------------------------
// Singleton instance
// -----------------------------

let _defaultRouter: WebSocketRouter | null = null;

/**
 * Get or create the default WebSocket router
 * Auto-initializes with default config if not already created
 */
export function getDefaultRouter(url?: string): WebSocketRouter {
    if (!_defaultRouter) {
        const routerUrl = url || getWebSocketUrl();
        _defaultRouter = createWebSocketRouter(routerUrl, {
            reconnectInterval: 5 * 60 * 1000, // 5 minutes
            maxReconnectAttempts: 5,
            heartbeatInterval: 30000,
            connectionMonitorInterval: 5 // Check connection health every 5 minutes
        });
    }
    return _defaultRouter;
}

/**
 * Get the router instance (alias for getDefaultRouter for compatibility)
 */
export function getRouter(): WebSocketRouter {
    return getDefaultRouter();
}

/**
 * Disconnect and reset the default router (for cleanup)
 */
export function disconnectRouter(): void {
    if (_defaultRouter) {
        _defaultRouter.disconnect();
        _defaultRouter = null;
    }
}

/**
 * Set the default WebSocket router
 */
export function setDefaultRouter(router: WebSocketRouter): void {
    _defaultRouter = router;
}

/**
 * Initialize WebSocket connection
 * This is the single entry point for establishing WebSocket connections
 */
export function initializeWebSocketConnection(): void {
    // Get or create router instance
    if (!_defaultRouter) {
        const url = getWebSocketUrl();
        _defaultRouter = createWebSocketRouter(url, {
            reconnectInterval: 5 * 60 * 1000, // 5 minutes
            maxReconnectAttempts: 5,
            heartbeatInterval: 30000,
            connectionMonitorInterval: 5 // Check connection health every 5 minutes
        });
    }
    
    const router = _defaultRouter;
    const currentState = router.getState();
    
    // Only connect if not already connected
    if (currentState !== 'connected') {
        router.connect();
    }
}
